import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import BeefreeSDK from "@beefree.io/sdk";
import ascentLogo from "media/images/app-logo.png";
import authService from "../api-authorization/AuthorizeService";
import style from "../../styles/scss/components/BeePreview.module.scss";
import { Spinner } from "utils/Spinner";
import { useDispatch, useSelector } from "react-redux";
import {
  clearDesignPreview,
  getDesignIdByPartitionKey,
  getDesignPreview,
} from "redux/actions";

export default function BeePreview() {
  const { partitionKey } = useParams();
  const dispatch = useDispatch();
  const {
    designPreview: { beeAuthResponse, json, html, loading, error },
  } = useSelector((state) => state.emailDesigner);

  const [emailName, setEmailName] = useState("");
  const [authData, setAuthData] = useState(null);
  const [template, setTemplate] = useState({ json: null, html: "" });

  const CONTAINER_ID = "bee-preview-container";
  const containerRef = useRef(null);
  const pluginRef = useRef(null);

  useEffect(() => {
    if (!partitionKey) {
      return;
    }

    // Dispatch action to get design preview
    dispatch(getDesignPreview(partitionKey));

    // Cleanup on unmount
    return () => {
      dispatch(clearDesignPreview());
    };
  }, [partitionKey, dispatch]);

  useEffect(() => {
    if (!beeAuthResponse || !json) return;

    try {
      // Parse Bee Auth
      const authBody = JSON.parse(beeAuthResponse);
      setAuthData(authBody);

      // Parse and set template
      let templateJson = json;
      if (typeof templateJson === "string") {
        try {
          templateJson = JSON.parse(templateJson);
        } catch (e) {
          console.error("Failed to parse template JSON:", e);
        }
      }
      setTemplate({ json: templateJson, html });
    } catch (err) {
      console.error("Error processing design preview data:", err);
    }
  }, [beeAuthResponse, json, html]);

  useEffect(() => {
    if (!authData || !template.json) return;

    const token = authData.access_token;
    if (!token) {
      console.error("No access token found in auth data");
      return;
    }

    // Check if container element exists in DOM
    const containerElement = document.getElementById(CONTAINER_ID);
    if (!containerElement) {
      console.error(`Container element with ID '${CONTAINER_ID}' not found`);
      return;
    }

    // Pass the full auth response object
    pluginRef.current = new BeefreeSDK(authData);

    const isMobileView = window.innerWidth < 500;

    const beeConfig = {
      uid: partitionKey,
      container: CONTAINER_ID, // Make sure this matches the actual DOM element ID
      language: "en",
      autosave: false,
      trackChanges: false,
      shared: true,
      disableCollaborative: true,
      workspace: {
        stage: isMobileView ? "mobile" : "desktop",
        hideStageToggle: true,
      },
      onLoad: () => {
        // Call preview after Bee has fully loaded
        if (pluginRef.current && pluginRef.current.preview) {
          setTimeout(() => {
            try {
              pluginRef.current.preview();
              
              if (isMobileView) {
                const styleId = "bee-preview-mobile-styles";
                if (!document.getElementById(styleId)) {
                  const style = document.createElement("style");
                  style.id = styleId;
                  style.textContent = `
                    #${CONTAINER_ID} {
                      display: flex !important;
                      justify-content: center !important;
                      align-items: flex-start !important;
                      overflow: auto !important;
                    }
                    #${CONTAINER_ID} > * {
                      flex-shrink: 0;
                    }
                  `;
                  document.head.appendChild(style);
                }
              }
              
              // Hide the close preview button
              const observer = new MutationObserver((mutations, obs) => {
                const btn = document.querySelector('[data-qa="close-preview"]');
                if (btn) {
                  btn.style.display = "none";
                  obs.disconnect();
                }
              });
              observer.observe(document.body, {
                childList: true,
                subtree: true,
              });
            } catch (previewError) {
              console.error("Error activating preview:", previewError);
            }
          }, 1000);
        }
      },
      onError: (error) => {
        console.error("Bee error:", error);
      },
    };    

    pluginRef.current
      .start(beeConfig, template.json)
      .then(() => {
      })
      .catch((err) => {
        console.error("BeePlugin start error:", err);
      });

    return () => {
      pluginRef.current = null;
    };
  }, [authData, template.json, partitionKey]);

  const handleLogin = async () => {
    const brandId = localStorage.getItem("brandId");
    const isAuth = await authService.isAuthenticated();

    if (isAuth) {
      try {
        const response = await dispatch(
          getDesignIdByPartitionKey(partitionKey)
        );
        const designId = response.content;
        window.location.href = `/EmailDesigner/BeePage/${designId}/${brandId}`;
      } catch (e) {
        console.error("Error fetching designId:", e);
      }
    } else {
      localStorage.setItem("pendingPartitionKey", partitionKey);
      window.location.href = "/";
    }
  };

  // Loading state
  if (loading) {
    return (
      <Spinner
        animation="border"
        style={{
          marginLeft: "45%",
          marginTop: "30%",
          marginBottom: "30%",
        }}
      />
    );
  }

  // Error state - Preview Unavailable
  if (error) {
    return (
      <div
        style={{
          margin: 0,
          padding: 0,
          fontFamily: "Roboto, sans-serif",
          height: "100vh",
          background: "linear-gradient(135deg, #31239A 0%, #402DCB 100%)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          {/* Ascent360 Logo */}
          <img
            className="ascent-logo"
            style={{ width: "320px" }}
            src={ascentLogo}
            alt="Ascent360 Logo"
          />
        </div>

        <div
          style={{
            maxWidth: "600px",
            padding: "0 20px",
          }}
        >
          <h1
            style={{
              color: "#FFF",
              fontSize: "24px",
              fontWeight: "600",
              fontFamily: "Roboto",
              marginBottom: "16px",
              lineHeight: "1.2",
              margin: "0 0 8px 0",
              textTransform: "capitalize",
            }}
          >
            This Email Preview is Unavailable.
          </h1>

          <p
            style={{
              color: "#FFF",
              fontSize: "16px",
              fontWeight: "400",
              lineHeight: "1.5",
              marginBottom: "8px",
              margin: "0",
            }}
          >
            This file doesn't exist or the public preview has been disabled.
          </p>

          <p
            style={{
              color: "#FFF",
              fontSize: "16px",
              fontWeight: "400",
              lineHeight: "1.5",
              margin: 0,
            }}
          >
            Ask the file owner to verify the link or enable the public link.
          </p>
        </div>
      </div>
    );
  }

  // Success state - Show normal BeePreview
  return (
    <div className={style.beePreviewPage}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1rem",
          borderBottom: "1px solid #e0e0e0",
          background: "#fff",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
        }}
      >
        <h2 style={{ margin: 0, fontSize: "1.25rem" }}>{emailName}</h2>
        <button
          onClick={handleLogin}
          style={{
            backgroundColor: "red",
            color: "#fff",
            border: "none",
            borderRadius: "25px",
            padding: "7px 30px",
          }}
        >
          Log In
        </button>
      </header>

      {/* leave space for fixed header */}
      <div style={{ height: "4rem" }} />

      <div
        id={CONTAINER_ID}
        ref={containerRef}
        style={{
          width: "100%",
          height: "calc(100vh - 4rem)",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      />
    </div>
  );
}